import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line
} from "recharts";
import { 
  Users, TrendingUp, TrendingDown, DollarSign, Package, 
  ChevronDown, ChevronRight, Download, RefreshCw, Calendar,
  ArrowUpRight, ArrowDownRight, Minus
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette for managers
const MANAGER_COLORS = {
  "MM": "#3b82f6",   // blue
  "IQ": "#10b981",   // emerald
  "ATU": "#f59e0b",  // amber
  "default": "#8b5cf6" // violet for others
};

const getManagerColor = (code) => MANAGER_COLORS[code] || MANAGER_COLORS.default;

const ManagerPerformancePage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  // Filters
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [compareYear, setCompareYear] = useState(null);
  const [compareMonth, setCompareMonth] = useState(null);
  const [expandedManagers, setExpandedManagers] = useState({});
  const [chartType, setChartType] = useState("bar");
  
  // Generate year/month options
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  }, []);
  
  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" },
    { value: 3, label: "March" }, { value: 4, label: "April" },
    { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" },
    { value: 9, label: "September" }, { value: 10, label: "October" },
    { value: 11, label: "November" }, { value: 12, label: "December" }
  ];

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/reports/manager-performance?year=${selectedYear}&month=${selectedMonth}`;
      if (compareYear && compareMonth) {
        url += `&compare_year=${compareYear}&compare_month=${compareMonth}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        toast.error("Failed to fetch report");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth, compareYear, compareMonth]);

  // Toggle manager expansion and scroll to drill-down
  const toggleManager = (code) => {
    setExpandedManagers(prev => {
      const newState = {
        ...prev,
        [code]: !prev[code]
      };
      
      // If expanding, scroll to the drill-down section after render
      if (!prev[code]) {
        setTimeout(() => {
          const element = document.getElementById(`drilldown-${code}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
      
      return newState;
    });
  };

  // Format currency
  const formatCurrency = (val) => {
    if (val === null || val === undefined) return "-";
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val);
  };

  // Format change indicator
  const formatChange = (current, previous, isCurrency = false) => {
    if (previous === null || previous === undefined) return null;
    const change = current - previous;
    const percent = previous !== 0 ? ((change / previous) * 100).toFixed(1) : 0;
    
    return {
      value: change,
      percent,
      isPositive: change > 0,
      isNegative: change < 0,
      display: isCurrency ? formatCurrency(Math.abs(change)) : Math.abs(change)
    };
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!data?.monthly_trend) return [];
    
    return data.monthly_trend.map(month => {
      const entry = { month: month.month };
      data.managers?.forEach(mgr => {
        entry[mgr.manager_code] = month.managers?.[mgr.manager_code]?.parcels || 0;
        entry[`${mgr.manager_code}_cost`] = month.managers?.[mgr.manager_code]?.cost || 0;
      });
      return entry;
    });
  }, [data]);

  // Export to CSV
  const handleExport = () => {
    if (!data?.managers) return;
    
    const rows = [["Manager", "Client", "Parcels", "Cost", "Sales", "Profit"]];
    
    data.managers.forEach(mgr => {
      // Manager total row
      rows.push([
        mgr.manager_code,
        `TOTAL (${mgr.client_count} clients)`,
        mgr.parcels,
        mgr.cost,
        mgr.sales,
        mgr.profit
      ]);
      
      // Client rows
      mgr.clients?.forEach(client => {
        rows.push([
          "",
          client.client_code + (client.name ? ` - ${client.name}` : ""),
          client.parcels,
          client.cost,
          client.sales,
          client.profit
        ]);
      });
    });
    
    const csvContent = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `manager_performance_${selectedYear}_${selectedMonth}.csv`;
    link.click();
    
    toast.success("Report exported");
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen" data-testid="manager-performance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Manager Performance Report</h1>
          <p className="text-slate-500 text-sm mt-1">
            Business performance of clients under each manager
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn">
            <Download className="w-4 h-4 mr-1" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Primary Period */}
            <div className="space-y-1">
              <label className="text-xs text-slate-500 font-medium">Primary Period</label>
              <div className="flex gap-2">
                <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                  <SelectTrigger className="w-32 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                  <SelectTrigger className="w-24 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Compare Period */}
            <div className="space-y-1">
              <label className="text-xs text-slate-500 font-medium">Compare With (Optional)</label>
              <div className="flex gap-2">
                <Select 
                  value={compareMonth ? String(compareMonth) : "none"} 
                  onValueChange={(v) => {
                    if (v === "none") {
                      setCompareMonth(null);
                      setCompareYear(null);
                    } else {
                      setCompareMonth(Number(v));
                      // Auto-set compare year if not already set
                      if (!compareYear) {
                        setCompareYear(selectedYear);
                      }
                    }
                  }}
                >
                  <SelectTrigger className="w-32 h-9">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No comparison</SelectItem>
                    {months.map(m => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {compareMonth && (
                  <Select 
                    value={compareYear ? String(compareYear) : String(selectedYear)} 
                    onValueChange={(v) => setCompareYear(Number(v))}
                  >
                    <SelectTrigger className="w-24 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {compareMonth && (
              <Badge variant="outline" className="h-9 px-3 bg-blue-50 text-blue-700 border-blue-200">
                <Calendar className="w-3 h-3 mr-1" />
                Comparing: {data?.period?.month_name} vs {data?.compare_period?.month_name}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="manager-cards-grid">
        {data?.managers?.map(mgr => (
          <Card 
            key={mgr.manager_code} 
            className="cursor-pointer hover:shadow-md transition-shadow border-l-4"
            style={{ borderLeftColor: getManagerColor(mgr.manager_code) }}
            onClick={() => toggleManager(mgr.manager_code)}
            data-testid={`manager-card-${mgr.manager_code}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: getManagerColor(mgr.manager_code) }}
                  >
                    {mgr.manager_code.substring(0, 2)}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{mgr.manager_code}</h3>
                    <p className="text-xs text-slate-500">{mgr.client_count} clients</p>
                  </div>
                </div>
                {expandedManagers[mgr.manager_code] ? (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-xs text-slate-500">Parcels</p>
                  <p className="font-semibold">{mgr.parcels.toLocaleString()}</p>
                  {mgr.parcels_change !== null && (
                    <div className={`flex items-center text-xs mt-1 ${mgr.parcels_change > 0 ? 'text-green-600' : mgr.parcels_change < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                      {mgr.parcels_change > 0 ? <ArrowUpRight className="w-3 h-3" /> : mgr.parcels_change < 0 ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                      {Math.abs(mgr.parcels_change)}
                    </div>
                  )}
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-xs text-slate-500">Sales</p>
                  <p className="font-semibold text-sm">{formatCurrency(mgr.sales)}</p>
                  {mgr.sales_change !== null && (
                    <div className={`flex items-center text-xs mt-1 ${mgr.sales_change > 0 ? 'text-green-600' : mgr.sales_change < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                      {mgr.sales_change > 0 ? <ArrowUpRight className="w-3 h-3" /> : mgr.sales_change < 0 ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                      {formatCurrency(Math.abs(mgr.sales_change))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-2 pt-2 border-t flex justify-between text-xs">
                <span className="text-slate-500">Profit: <span className={mgr.profit >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>{formatCurrency(mgr.profit)}</span></span>
                <span className="text-slate-500">{mgr.profit_margin}% margin</span>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {/* Grand Total Card */}
        {data?.grand_totals && (
          <Card className="border-l-4 border-l-slate-700 bg-slate-800 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-6 h-6" />
                <div>
                  <h3 className="font-bold">Total</h3>
                  <p className="text-xs text-slate-300">{data.grand_totals.clients} clients</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-slate-700 rounded p-2">
                  <p className="text-xs text-slate-400">Parcels</p>
                  <p className="font-semibold">{data.grand_totals.parcels.toLocaleString()}</p>
                </div>
                <div className="bg-slate-700 rounded p-2">
                  <p className="text-xs text-slate-400">Sales</p>
                  <p className="font-semibold text-sm">{formatCurrency(data.grand_totals.sales)}</p>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-slate-600 text-xs">
                Profit: <span className={data.grand_totals.profit >= 0 ? 'text-green-400' : 'text-red-400'}>{formatCurrency(data.grand_totals.profit)}</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Monthly Trend Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              Monthly Trend (Last 6 Months)
            </CardTitle>
            <Tabs value={chartType} onValueChange={setChartType}>
              <TabsList className="h-8">
                <TabsTrigger value="bar" className="text-xs px-3">Bar</TabsTrigger>
                <TabsTrigger value="line" className="text-xs px-3">Line</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {chartType === "bar" ? (
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ fontSize: 12 }}
                    formatter={(value, name) => [value.toLocaleString(), name]}
                  />
                  <Legend />
                  {data?.managers?.map(mgr => (
                    <Bar 
                      key={mgr.manager_code}
                      dataKey={mgr.manager_code} 
                      name={mgr.manager_code}
                      fill={getManagerColor(mgr.manager_code)}
                      radius={[4, 4, 0, 0]}
                    />
                  ))}
                </BarChart>
              ) : (
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ fontSize: 12 }}
                    formatter={(value, name) => [value.toLocaleString(), name]}
                  />
                  <Legend />
                  {data?.managers?.map(mgr => (
                    <Line 
                      key={mgr.manager_code}
                      type="monotone"
                      dataKey={mgr.manager_code} 
                      name={mgr.manager_code}
                      stroke={getManagerColor(mgr.manager_code)}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Table (if comparison enabled) */}
      {data?.compare_period && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">
              Month Comparison: {data.period.month_name} vs {data.compare_period.month_name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-medium">Manager</th>
                    <th className="text-right p-3 font-medium">{data.period.month_name}</th>
                    <th className="text-right p-3 font-medium">{data.compare_period.month_name}</th>
                    <th className="text-right p-3 font-medium">Change</th>
                    <th className="text-right p-3 font-medium">% Growth</th>
                  </tr>
                </thead>
                <tbody>
                  {data.managers?.map(mgr => {
                    const change = formatChange(mgr.sales, mgr.compare_sales, true);
                    return (
                      <tr key={mgr.manager_code} className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: getManagerColor(mgr.manager_code) }}
                            />
                            <span className="font-medium">{mgr.manager_code}</span>
                            <Badge variant="outline" className="text-xs">{mgr.client_count} clients</Badge>
                          </div>
                        </td>
                        <td className="p-3 text-right font-medium">{formatCurrency(mgr.sales)}</td>
                        <td className="p-3 text-right text-slate-600">{formatCurrency(mgr.compare_sales)}</td>
                        <td className="p-3 text-right">
                          {change && (
                            <span className={change.isPositive ? 'text-green-600' : change.isNegative ? 'text-red-600' : ''}>
                              {change.isPositive ? '+' : ''}{change.value < 0 ? '-' : ''}{change.display}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {change && (
                            <div className={`flex items-center justify-end gap-1 ${change.isPositive ? 'text-green-600' : change.isNegative ? 'text-red-600' : 'text-slate-500'}`}>
                              {change.isPositive ? <ArrowUpRight className="w-4 h-4" /> : change.isNegative ? <ArrowDownRight className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                              {Math.abs(change.percent)}%
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client Drill-down Tables */}
      {data?.managers?.map(mgr => expandedManagers[mgr.manager_code] && (
        <Card 
          key={`detail-${mgr.manager_code}`} 
          id={`drilldown-${mgr.manager_code}`}
          className="border-l-4" 
          style={{ borderLeftColor: getManagerColor(mgr.manager_code) }}
          data-testid={`drilldown-${mgr.manager_code}`}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: getManagerColor(mgr.manager_code) }}
              >
                {mgr.manager_code.substring(0, 2)}
              </div>
              {mgr.manager_code} - Client Breakdown
              <Badge variant="outline">{mgr.client_count} clients</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-2 font-medium">Client</th>
                    <th className="text-right p-2 font-medium">Parcels</th>
                    <th className="text-right p-2 font-medium">Cost</th>
                    <th className="text-right p-2 font-medium">Sales</th>
                    <th className="text-right p-2 font-medium">Profit</th>
                    {data.compare_period && (
                      <>
                        <th className="text-right p-2 font-medium text-slate-500">Prev Parcels</th>
                        <th className="text-right p-2 font-medium text-slate-500">Prev Sales</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {mgr.clients?.map(client => (
                    <tr key={client.client_code} className="border-b hover:bg-slate-50">
                      <td className="p-2">
                        <div>
                          <span className="font-medium">{client.client_code}</span>
                          {client.name && <span className="text-slate-500 text-xs ml-2">{client.name}</span>}
                        </div>
                      </td>
                      <td className="p-2 text-right">{client.parcels.toLocaleString()}</td>
                      <td className="p-2 text-right text-slate-600">{formatCurrency(client.cost)}</td>
                      <td className="p-2 text-right font-medium">{formatCurrency(client.sales)}</td>
                      <td className={`p-2 text-right font-medium ${client.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(client.profit)}
                      </td>
                      {data.compare_period && (
                        <>
                          <td className="p-2 text-right text-slate-500">{client.compare_parcels?.toLocaleString() || '-'}</td>
                          <td className="p-2 text-right text-slate-500">{formatCurrency(client.compare_sales)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                  {/* Total row */}
                  <tr className="bg-slate-100 font-medium">
                    <td className="p-2">Total</td>
                    <td className="p-2 text-right">{mgr.parcels.toLocaleString()}</td>
                    <td className="p-2 text-right">{formatCurrency(mgr.cost)}</td>
                    <td className="p-2 text-right">{formatCurrency(mgr.sales)}</td>
                    <td className={`p-2 text-right ${mgr.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(mgr.profit)}
                    </td>
                    {data.compare_period && (
                      <>
                        <td className="p-2 text-right text-slate-500">{mgr.compare_parcels?.toLocaleString() || '-'}</td>
                        <td className="p-2 text-right text-slate-500">{formatCurrency(mgr.compare_sales)}</td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ManagerPerformancePage;
